import { useState, useEffect, useRef } from 'react';
import { referralAPI } from '../api/client';
import { useToastStore } from '../store/toastStore';
import { track } from '../utils/analytics';
import BottomSheet from './BottomSheet';

export default function ReferralCodeSheet({ isOpen, onClose, userId, initialCode = '' }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const showToast = useToastStore(s => s.showToast);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setCode(initialCode.toUpperCase().replace(/[^A-Z0-9]/g, ''));
      setError(null);
      setSuccess(false);
    }
  }, [isOpen, initialCode]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 400);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleCodeChange = (e) => {
    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
    setCode(val);
    setError(null);
  };

  const handleApply = async () => {
    if (code.length !== 6) {
      setError('6자리 코드를 입력해주세요');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await referralAPI.apply(userId, code);
      if (res.success) {
        track('referral-applied');
        setSuccess(true);
        showToast('1주 무료 체험이 시작됐어요!', 'success');
        setTimeout(() => onClose(true), 1800);
      }
    } catch (err) {
      const errCode = err.response?.data?.error;
      const messages = {
        already_used: '이미 초대 코드를 사용했어요',
        invalid_code: '유효하지 않은 코드예요',
        own_code: '본인의 코드는 사용할 수 없어요',
        code_exhausted: '이 코드는 사용 횟수가 초과됐어요',
      };
      setError(messages[errCode] || '코드 적용에 실패했어요');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && code.length === 6 && !loading) {
      handleApply();
    }
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={() => !loading && !success && onClose(false)} zIndex={50}>
      <div className="max-w-lg mx-auto px-6 pb-8">
        {success ? (
          /* 성공 상태 */
          <div className="text-center py-6">
            <div className="relative w-20 h-20 mx-auto mb-5">
              <div className="absolute inset-0 bg-tayo/10 rounded-full animate-pulse-glow" />
              <div className="relative w-20 h-20 bg-gradient-to-br from-tayo to-tayo-dark rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-charcoal mb-2">1주 무료 체험 시작!</h3>
            <p className="text-sm text-sub-text leading-relaxed">
              모든 기능을 자유롭게 이용해보세요
            </p>
          </div>
        ) : (
          /* 코드 입력 상태 */
          <>
            {/* 아이콘 + 타이틀 */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-tayo-light rounded-2xl mb-4">
                <svg className="w-7 h-7 text-tayo" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-charcoal mb-1.5">초대 코드가 있으신가요?</h3>
              <p className="text-sm text-sub-text leading-relaxed">
                친구의 초대 코드를 입력하면<br />
                <span className="font-semibold text-tayo">1주 무료 체험</span>을 바로 시작할 수 있어요
              </p>
            </div>

            {/* 코드 입력 필드 */}
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                inputMode="text"
                autoCapitalize="characters"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                value={code}
                onChange={handleCodeChange}
                onKeyDown={handleKeyDown}
                placeholder="XXXXXX"
                maxLength={6}
                className={`w-full text-center text-[28px] font-bold tracking-[0.35em] pl-[0.35em] py-4 border-2 rounded-2xl transition-all duration-200 outline-none bg-snow ${
                  error
                    ? 'border-coral/60 focus:border-coral'
                    : code.length === 6
                      ? 'border-tayo/40 focus:border-tayo'
                      : 'border-border focus:border-tayo/60'
                }`}
              />
              {/* 글자 수 인디케이터 */}
              <div className="flex justify-center gap-1.5 mt-3">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className={`h-1 rounded-full transition-all duration-200 ${
                      i < code.length
                        ? 'w-5 bg-tayo'
                        : 'w-3 bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* 에러 메시지 */}
            {error && (
              <div className="flex items-center justify-center gap-1.5 mt-3 animate-fade-up">
                <svg className="w-4 h-4 text-coral flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p className="text-sm text-coral font-medium">{error}</p>
              </div>
            )}

            {/* 버튼들 */}
            <button
              onClick={handleApply}
              disabled={code.length !== 6 || loading}
              className="btn-press w-full mt-5 py-3.5 bg-gradient-to-r from-tayo to-tayo-dark text-white font-bold rounded-2xl disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-lg"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  적용 중...
                </span>
              ) : (
                '코드 적용하기'
              )}
            </button>

            <button
              onClick={() => onClose(false)}
              disabled={loading}
              className="w-full mt-2 py-3 text-sub-text text-sm font-medium hover:text-charcoal transition-colors"
            >
              나중에 할게요
            </button>
          </>
        )}
      </div>
    </BottomSheet>
  );
}
